import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./ChatAI.scss";
import { ConversationProps } from "../../types/chat-bot-hub/ConversationProps";
import TypingLoader from "./TypingLoader";
import { MessageData } from "../../types/chat-bot-hub/MessageData";
import DOMPurify from "dompurify";

type MessageGroups = {
  [date: string]: MessageData[];
};

const Conversation: React.FC<ConversationProps> = ({ messages, sendTextToChatBot, responseCompleted,fetchLabelKeyTranslation }) => {
  const [localInputText, setLocalInputText] = useState<string>("");
  const [localIsProcessing, setLocalIsProcessing] = useState(false);
  const [messageGroups, setMessageGroups] = useState<MessageGroups>({});
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputWrapperRef = useRef<HTMLDivElement | null>(null);
  const chatBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLocalIsProcessing(!responseCompleted);
  }, [responseCompleted]);

  useEffect(() => {
    setMessageGroups(groupMessagesByDate());
  }, [messages]);
  
  const messagesEndRef = useRef<HTMLDivElement | null>(null);


  useLayoutEffect(() => {
    if (messages.length || messages[messages.length-1]?.answers?.length) {
      setTimeout(() => scrollToBottom("auto"), 0);
    }
  }, [messages.length,messages[messages.length-1]?.answers?.length]);
  

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const el = chatBoxRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  };

  const formatDateHeader = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset time part for comparison
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() === today.getTime()) {
      return "Today";
    } else if (date.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Helper function to get date string (YYYY-MM-DD) from timestamp
  const getDateString = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };
  // Group messages by date
  const groupMessagesByDate = () => {
    const groups: { [date: string]: typeof messages } = {};
    messages.forEach(msg => {
      if (msg.questions && msg.questions.timeStamp) {
        const dateKey = getDateString(msg.questions.timeStamp.toString());
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(msg);
      }
    });

    return groups;
  };


  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleString("en-US", {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };
  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalInputText(event.target.value);
  }

  const handleClick = () => {
    if (!localInputText.trim()) return;

    sendTextToChatBot(
      messages.find(m => m.sessionId)?.sessionId ?? "",
      localIsProcessing,
      localInputText
    );

    setLocalIsProcessing(true);
    setLocalInputText("");

    scrollToBottom("auto");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleClick();
    }
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const wrapper = wrapperRef.current;

    if (!textarea || !wrapper) return;

    const lineHeight = parseFloat(
      window.getComputedStyle(textarea).lineHeight
    );

    const maxLines = 5;
    const maxHeight = lineHeight * maxLines + 16;

    textarea.style.height = "auto";
    textarea.style.height =
      Math.min(textarea.scrollHeight, maxHeight) + "px";

    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? "auto" : "hidden";

    document.documentElement.style.setProperty(
      "--chat-input-height",
      `${wrapper.offsetHeight}px`
    );

    scrollToBottom("auto");
  }, [localInputText]);

  return (
    <>
      <div className="h-100 chat-layout">
        <div className="chat-box" ref={chatBoxRef}>
          {Object.entries(messageGroups).map(([dateKey, messagesInGroup]) => (
            <div key={dateKey}>
              <div className="text-center my-3">
                <span className="px-3 py-1 text-muted" style={{
                  fontWeight: 'normal',
                  fontSize: '0.875rem'
                }}>
                  {formatDateHeader(messagesInGroup[0].questions.timeStamp.toString())}
                </span>
              </div>

              {/* Messages for this date */}
              {messagesInGroup.map((m, i) => (
                <div key={i}>
                  {/* Request */}
                  {m.questions && m.questions.text !== "" && (
                    <div className="media ml-auto mb-3 justify-content-end max-w">
                      <div className="d-flex flex-column align-items-end">
                        <div className="bg-primary mb-1 custom-chat chat-sender">
                          <div className="text-small mb-0 text-white">
                            {m.questions.text}
                          </div>
                        </div>
                        <p className="timestamp text-muted mb-0">
                          {formatTimestamp(m.questions.timeStamp)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Result */}
                  {m.answers && m.answers.length > 0 && (
                    <div className="media max-w mb-3">
                      <div className="ml-3">
                        <div className="bg-white mb-1 custom-chat chat-reciever">
                          <div className="flex flex-col gap-2 text-small mb-0 text-muted">
                            {m.answers.map((r, j) => (
                              <p key={j} style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.text) }} />
                            ))}
                          </div>
                        </div>
                        <p className="text-muted timestamp text-left" style={{ marginLeft: "22px" }}>
                          {formatTimestamp(m.answers[m.answers.length - 1].timeStamp)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <form action="#" className="message-input">
          {localIsProcessing && <TypingLoader />}
          <div ref={wrapperRef} className="mt-4 relative chat-input-wrapper mb-2">
            <div className="d-flex align-items-center shadowsm typingarea bg-white">
              <textarea
                ref={textareaRef}
                rows={1}
                value={localInputText}
                className="form-control border-0 shadow-.none ms-2 bg-transparent chat-textarea"
                placeholder={fetchLabelKeyTranslation("AskAnythingPlaceholder","Ask anything.....")}
                onChange={(e) => setLocalInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleClick();
                  }
                }}
                disabled={localIsProcessing}
              >
              </textarea>

              <button
                type="button"
                className="btn rounded-circle d-flex align-items-center justify-content-center me-1 chatbtn"
                onClick={handleClick}
                disabled={localIsProcessing}
              >
                <i className="bi bi-arrow-up text-white"></i>
                {/* {!localIsProcessing ?<i className="bi bi-arrow-up text-white"></i> :
            <i className="bi bi-stop-circle text-white"></i>} */}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default Conversation;
